import React, { createContext, useContext, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import jwt from 'jwt-decode';
import api from '../../services/api';

const SessionContext = createContext();

function SessionProvider({ children }) {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);

  const [_token, setToken] = useState(() => {
    return localStorage.getItem('token') || null;
  });
  const [agent, setAgent] = useState(() => {
    return JSON.parse(localStorage.getItem('agent')) || null;
  });

  const [isAssessoryMenuSelected, setIsAssessoryMenuSelected] = useState(false);

  const getExpirationDate = useCallback(token => {
    if (!token) {
      return null;
    }
    const jwtObj = JSON.parse(atob(token.split('.')[1]));
    return (jwtObj && jwtObj.exp && jwtObj.exp * 1000) || null;
  }, []);

  const isExpired = useCallback(exp => {
    if (!exp) {
      return false;
    }
    return Date.now() > exp;
  }, []);

  const isLoggedIn = useCallback(() => {
    const hasExpired = isExpired(getExpirationDate(_token));
    return !!localStorage.getItem('token') || hasExpired;
  }, []);

  const loadAgentData = useCallback(async token => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const { sub } = jwt(token);

      const response = await api.get(`/api/agents/${sub}/details`, config);
      const { data } = response;

      localStorage.setItem('agent', JSON.stringify(data));
      setAgent(data);

      history.push('/home');
    } catch (err) {
      if (err.response) {
        console.log(err.response.data);
      } else {
        console.log(err);
      }
    }
  }, []);

  const doLogin = useCallback(async body => {
    try {
      const config = {
        method: 'post',
        url: '/agents/auth',
        headers: {
          'Content-Type': 'application/json',
        },
        data: body,
      };

      setIsLoading(true);

      const response = await api.request(config);
      const { data } = response;

      if (data && data.token) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setIsLoading(false);
        loadAgentData(data.token);
      } else {
        throw new Error('Falha ao efetuar login');
      }
    } catch (err) {
      if (err.response) {
        console.log(err.response.data);
      } else {
        console.log(err);
      }
    }
  }, []);

  const doLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('agent');

    setToken(null);
    setAgent(null);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        agent,
        setAgent,
        isAssessoryMenuSelected,
        isLoading,
        doLogin,
        doLogout,
        setIsAssessoryMenuSelected,
        setIsLoading,
        _token,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error(
      'You must surround your components with a SessionProvider before call useSession',
    );
  }

  return context;
};

export default SessionProvider;
